import { Field } from 'react-final-form';
import { useTranslation } from 'react-i18next';

import { FormSelect } from '../../../../../../../styledComponents/UI/Form/FormSelect/FormSelect';
import { getFieldPropertiesForGroup } from '../../../../../../../utils/getFormGroupPath';

import { IOrganizationSubscriptionPlanModel } from '../../../../../../../models/OrganizationSubscriptionPlanModel';
import { useMembershipOrganizationDetails } from './aaMembershipOrganizationDetailsForm.hooks';
import { IOrganizationSearch } from '../../../../../../../models/interfaces/SearchOrganization';
import { formatDate } from '../../../../../../../utils/dateHelpers';
import { FormColumn } from '../../../../../../../styledComponents/UI/Form/FormColumn/FormColumn';
import { FormRow } from '../../../../../../../styledComponents/UI/Form/FormRow/FormRow';
import { FormInput } from '../../../../../../../styledComponents/UI/Form/FormInput/FormInput';
import FormDatePicker from '../../../../../../../styledComponents/UI/Form/FormDatePicker/FormDatePicker';
import { FormSelectAsync } from '../../../../../../../styledComponents/UI/Form/FormSelectAsync/FormSelectAsync';

import FormValue from '../../../../../../../styledComponents/UI/Form/FormValue/FormValue';
import {
  formName,
  MembershipOrganizationDetailsFormFields,
} from './aaMembershipOrganizationDetailsForm.models';
import { SectionWrapper } from '../../../../../../../styledComponents/Form/Form';
import {
  StyledFormMessage,
  StyledFormValueInfo,
} from './aaMembershipOrganizationDetailsForm.styles';
import { CustomForm } from '../../../../../../../models/types/CustomForm';
import { FormEmptyField } from '../../../../../../../styledComponents/UI/Form/FormEmptyField/FormEmptyField';

const MembershipOrganizationDetailsForm = ({
  className,
  disabled,
}: CustomForm) => {
  const { t } = useTranslation();
  const {
    subscriptionsOptions,
    organizationsPromiseOptions,
    organizationSubscriptionPlansPromiseOptions,
    selectedPlan,
    getExtensionPrice,
    clearPromoCode,
    loadPromoCode,
  } = useMembershipOrganizationDetails();

  return (
    <SectionWrapper className={className}>
      <FormColumn>
        <FormRow isSubRow>
          <Field
            component={FormInput}
            {...getFieldPropertiesForGroup({
              inputId: `${formName}}`,
              name: MembershipOrganizationDetailsFormFields.PromoCode,
            })}
            onBlur={() => {
              loadPromoCode();
            }}
          />
          <Field
            name={MembershipOrganizationDetailsFormFields.SearchedPromoCode}
            component={FormValue}
          />
          <Field
            component={FormSelectAsync}
            isClearable
            disabled={disabled}
            label={t('fields.organizationName.label')}
            labelSelector={(value: IOrganizationSearch) => value.name}
            {...getFieldPropertiesForGroup({
              inputId: `${formName}`,
              name: MembershipOrganizationDetailsFormFields.Organization,
            })}
            onOptionSelected={() => clearPromoCode()}
            placeholder={t('fields.organizationName.placeholder')}
            promiseOptions={organizationsPromiseOptions}
          />
        </FormRow>
        <FormRow isSubRow>
          <Field
            component={FormSelect}
            disabled={disabled}
            isLabelHidden
            {...getFieldPropertiesForGroup({
              inputId: `${formName}`,
              name: MembershipOrganizationDetailsFormFields.SubscriptionType,
            })}
            options={subscriptionsOptions}
            placeholder={t('fields.subscriptionType.placeholder')}
            setDefaultValue
          />
          <FormRow isSubRow>
            <Field
              component={FormSelect}
              disabled={disabled}
              isLabelHidden
              {...getFieldPropertiesForGroup({
                inputId: `${formName}`,
                name: MembershipOrganizationDetailsFormFields.MembershipPlanId,
              })}
              promiseOptions={organizationSubscriptionPlansPromiseOptions}
              placeholder={t('fields.membershipPlanId.placeholder')}
              valueIdSelector={(value: IOrganizationSubscriptionPlanModel) =>
                value.membershipPlanId
              }
            />

            <StyledFormValueInfo
              label={t('fields.extensionPrice.label')}
              isBoldValue
              value={getExtensionPrice()}
            />
          </FormRow>
        </FormRow>
        <FormRow>
          <FormRow isSubRow>
            <Field
              component={FormDatePicker}
              disabled={disabled}
              {...getFieldPropertiesForGroup({
                inputId: `${formName}`,
                name: MembershipOrganizationDetailsFormFields.EffectiveDate,
              })}
            />
            <Field
              label={t('fields.expirationDate.label')}
              component={FormValue}
              contentVisible
              {...getFieldPropertiesForGroup({
                inputId: `${formName}`,
                name: MembershipOrganizationDetailsFormFields.ExpirationDate,
              })}
              valueRenderer={(value: string) => formatDate(value)}
              value={null}
            />
          </FormRow>
          <FormEmptyField />
        </FormRow>
      </FormColumn>
      {selectedPlan && (
        <StyledFormMessage
          message={t(
            selectedPlan.availableSeats > 0
              ? 'phrase.seats_remaining'
              : 'phrase.seats_remaining_add_required',
            {
              value: selectedPlan.availableSeats,
            },
          )}
          status={selectedPlan.availableSeats > 0 ? 'success' : 'warning'}
        />
      )}
    </SectionWrapper>
  );
};

export default MembershipOrganizationDetailsForm;
